import FormField, { fieldControlA11yProps } from "lib/components/Form/FormField"
import { fieldBox, fieldControl, resolveFieldStatus } from "lib/components/Form/fieldStyles"
import { ReactNode, forwardRef, useCallback, useEffect, useId, useRef } from "react"

export interface TextAreaProps {
  label?: string;
  success?: boolean;
  error?: boolean;
  message?: string | string[];
  /** Neutral hint under the box; replaced by `message` while `error` is set. */
  helperText?: ReactNode
  disabled?: boolean;
  placeholder?: string;
  onChange?: (object) => void;
  value?: string;
  name: string;
  maxLength?: number;
  rows: number;
  noBorder?: boolean
  /** Grows the field to fit its content by writing `style.height` on every change. */
  autoResize?: boolean
}

const TextArea = forwardRef<HTMLTextAreaElement | null, TextAreaProps>(({
  label, error, success, message, helperText, disabled = false, placeholder, onChange, value, name, maxLength, rows, noBorder = false,
  autoResize = false,
}: TextAreaProps, forwardedRef) => {
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null)
  const controlId = useId()
  const status = resolveFieldStatus(error, success)
  const helper = status === "danger" && message ? message : helperText

  // Both refs must land: the internal one drives auto-resize, the forwarded one is what
  // react-hook-form registers.
  const setRefs = useCallback(
    (node: HTMLTextAreaElement | null) => {
      textAreaRef.current = node
      if (typeof forwardedRef === "function") forwardedRef(node)
      else if (forwardedRef) forwardedRef.current = node
    },
    [forwardedRef],
  )

  const resizeTextArea = () => {
    if (!textAreaRef.current) return
    textAreaRef.current.style.height = "auto"
    textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`
  }

  useEffect(() => {
    if (autoResize) resizeTextArea()
  }, [value, autoResize])

  return (
    <FormField
      controlId={controlId}
      label={label}
      name={name}
      status={status}
      disabled={disabled}
      helperText={helperText}
      message={message}>
      <div className="relative flex-1">
        <textarea
          className={`${fieldBox({
            status,
            disabled,
            // Never fixed: `rows` and autoResize's inline style own the height.
            height: "auto",
            border: noBorder ? "none" : "full",
            focusMode: "self",
          })} ${fieldControl({ disabled })} py-2 placeholder:text-sm ${autoResize ? "overflow-y-hidden resize-none" : ""}`}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(event) => {
            onChange?.(event)
            if (autoResize) resizeTextArea()
          }}
          value={value}
          name={name}
          maxLength={maxLength}
          rows={rows}
          ref={setRefs}
          {...fieldControlA11yProps({ controlId, status, hasHelper: Boolean(helper) })}
        />
      </div>
    </FormField>
  )
})

TextArea.displayName = "TextArea"

export default TextArea
